<template>
	<div class="relative w-full">
		<!-- Viewbox -->
		<svg
			class="block w-full h-auto overflow-visible select-none"
			:viewBox="viewBox"
			role="img"
			aria-label="Personality wheel"
		>
			<!-- White dotted pattern -->
			<defs>
				<pattern
					id="jobDots"
					patternUnits="userSpaceOnUse"
					width="6"
					height="6"
				>
					<circle cx="3" cy="3" r="1" fill="rgba(255,255,255,0.75)" />
				</pattern>
			</defs>

			<!-- Wheel -->
			<g :transform="`translate(${cx} ${cy})`">
				<!-- Arcs -->
				<g
					v-for="(arc, index) in arcs"
					:key="arc.id"
					class="cursor-pointer"
					@mouseenter="onArcEnter(index)"
					@mouseleave="onArcLeave(index)"
				>
					<!-- Outer arc -->
					<path :d="outerPath(index)" :fill="arc.colorOuter" />

					<!-- Inner arcs -->
					<template v-if="mode === 'comparison'">
						<!-- Applicant -->
						<path
							:d="innerPathHalf(index, 'applicant')"
							:fill="arc.colorInner"
						/>

						<!-- Job offer -->
						<path
							:d="innerPathHalf(index, 'job')"
							:fill="arc.colorInner"
						/>

						<path
							:d="innerPathHalf(index, 'job')"
							fill="url(#jobDots)"
							opacity="1"
						/>

						<!-- Separator line -->
						<path
							:d="separatorPath(index)"
							:stroke="arc.colorOuter"
							:stroke-width="separatorStrokeWidth"
							stroke-linecap="butt"
							fill="none"
						/>
					</template>

					<template v-else>
						<path :d="innerPath(index)" :fill="arc.colorInner" />
					</template>

					<!-- Badge + icon -->
					<g
						:opacity="deployProgress"
						:transform="
							`translate(${iconPos(index).x} ${
								iconPos(index).y
							}) scale(${deployProgress})`
						"
					>
						<circle
							:ref="`badge-${index}`"
							:r="badgeRadius"
							:fill="getBadgeBgColor(arc)"
							:stroke="getBadgeStrokeColor(arc)"
							:stroke-width="badgeStrokeWidth"
						/>

						<foreignObject
							:width="iconSize"
							:height="iconSize"
							:x="-iconSize / 2"
							:y="-iconSize / 2"
						>
							<component
								:is="getIconComponent(arc.name)"
								:size="iconSize"
								color="#000000"
								:stroke-width="1.4"
							/>
						</foreignObject>
					</g>
				</g>
			</g>
		</svg>

		<!-- Quandrant labels -->
		<div
			:style="{ opacity: deployProgress }"
			class="absolute inset-0 pointer-events-none"
			aria-hidden="true"
		>
			<span
				v-for="label in axisLabels"
				:key="label.id"
				:data-testid="`personality-wheel-axis-label-${label.id}`"
				:style="label.style"
				class="absolute mb-0 text-xs leading-none tracking-widest text-white uppercase whitespace-nowrap personality-wheel-axis-label"
			>
				{{ $t(label.translationKey) }}
			</span>
		</div>

		<!-- Tooltip -->
		<Tooltip
			:open="tooltipOpen"
			:reference="tooltipRef"
			placement="top"
			:offset="40"
			:use-portal="true"
			:interactive="false"
			:flip="false"
		>
			<span class="hidden" />
			<template #content>
				<div v-if="tooltipArc">
					<div class="capitalize ">
						{{ tooltipArc.displayName }}
					</div>
					<div class="text-xs opacity-70">
						{{ tooltipArc.subtitle }}
					</div>

					<!-- Tooltip comparison view -->
					<div v-if="mode === 'comparison'" class="mt-2 space-y-1">
						<!-- Applicant -->
						<div class="flex items-center justify-between gap-4">
							<span class="text-xs opacity-70">
								{{ $t('personality_results.mode.applicant') }}
							</span>
							<span class="font-semibold">
								{{ tooltipArc.applicantValue }}%
							</span>
						</div>

						<!-- Job profile -->
						<div class="flex items-center justify-between gap-4">
							<span class="text-xs opacity-70">
								{{ $t('personality_results.mode.job_profile') }}
							</span>
							<span class="font-semibold">
								{{ tooltipArc.jobOfferValue }}%
							</span>
						</div>
					</div>

					<!-- Tooltip simple view (applicant & job profile) -->
					<div v-else class="mt-1 font-semibold">
						{{ tooltipArc.value }}%
					</div>
				</div>
			</template>
		</Tooltip>
	</div>
</template>

<script>
import { arc } from 'd3-shape'
import Tooltip from '~/components/Tooltip/Tooltip'
import { getTraitIcon } from '~/components/Icons/traits'
import { TRAIT_LABELS } from '~/components/Personality/utils/personalityConstants.js'

export default {
	name: 'PersonalityWheel',
	components: {
		Tooltip
	},
	inject: {
		personalityResultsExport: { default: null }
	},
	props: {
		results: {
			type: Object,
			required: true
		},
		applicantResults: {
			type: Object,
			required: false,
			default: null
		},
		jobOfferResults: {
			type: Object,
			required: false,
			default: null
		},
		mode: {
			type: String,
			required: false,
			default: 'applicant' // applicant | job | comparison
		},
		animated: {
			type: Boolean,
			required: false,
			default: false
		}
	},
	data() {
		return {
			size: 520,
			viewBoxPadding: 36,
			rOuter: 230,
			rOuterInner: 0,
			rInnerInner: 0,
			axisLabelRadius: 258,
			axisSideLabelYOffset: -16,
			badgeRadius: 27,
			badgeStrokeWidth: 1.5,
			badgeStrokeColorActive: '#fff',
			badgeStrokeColorInactive: '#808080',
			iconSize: 26,
			iconFillColor: '#000',
			deployT: 1,
			deployMs: 900,
			hoverIndex: null,
			hoverScale: 1.05,
			hoverMs: 200,
			hoverProgressByIndex: Object.create(null),
			hoverRAFByIndex: Object.create(null),
			tooltipIndex: null,
			tooltipRef: null,
			separatorStrokeWidth: 1,
			arcGen: arc(),
			baseArcs: [
				{
					id: 'reformer',
					name: 'reformer',
					displayName: this.displayTraitName('reformer'),
					subtitle: this.displayTraitSlogan('reformer'),
					colorOuter: '#34333A',
					colorInner: '#838091',
					badgeBgColorInactive: '#34333A',
					badgeBgColorActive: '#838091',
					iconText: 'refo'
				},
				{
					id: 'initiator',
					name: 'initiator',
					displayName: this.displayTraitName('initiator'),
					subtitle: this.displayTraitSlogan('initiator'),
					colorOuter: '#7F222C',
					colorInner: '#D43949',
					badgeBgColorInactive: '#7F222C',
					badgeBgColorActive: '#D43949',
					iconText: 'init'
				},
				{
					id: 'motivator',
					name: 'motivator',
					displayName: this.displayTraitName('motivator'),
					subtitle: this.displayTraitSlogan('motivator'),
					colorOuter: '#603020',
					colorInner: '#EF7950',
					badgeBgColorInactive: '#603020',
					badgeBgColorActive: '#EF7950',
					iconText: 'moti'
				},
				{
					id: 'inspirator',
					name: 'inspirator',
					displayName: this.displayTraitName('inspirator'),
					subtitle: this.displayTraitSlogan('inspirator'),
					colorOuter: '#665213',
					colorInner: '#FECC30',
					badgeBgColorInactive: '#665213',
					badgeBgColorActive: '#FECC30',
					iconText: 'insp'
				},
				{
					id: 'advisor',
					name: 'advisor',
					displayName: this.displayTraitName('advisor'),
					subtitle: this.displayTraitSlogan('advisor'),
					colorOuter: '#4B511E',
					colorInner: '#BBCA4A',
					badgeBgColorInactive: '#4B511E',
					badgeBgColorActive: '#BBCA4A',
					iconText: 'advi'
				},
				{
					id: 'supporter',
					name: 'supporter',
					displayName: this.displayTraitName('supporter'),
					subtitle: this.displayTraitSlogan('supporter'),
					colorOuter: '#2A4B38',
					colorInner: '#68BC8D',
					badgeBgColorInactive: '#2A4B38',
					badgeBgColorActive: '#68BC8D',
					iconText: 'supp'
				},
				{
					id: 'coordinator',
					name: 'coordinator',
					displayName: this.displayTraitName('coordinator'),
					subtitle: this.displayTraitSlogan('coordinator'),
					colorOuter: '#1D484B',
					colorInner: '#49B4BC',
					badgeBgColorInactive: '#1D484B',
					badgeBgColorActive: '#49B4BC',
					iconText: 'coor'
				},
				{
					id: 'observer',
					name: 'observer',
					displayName: this.displayTraitName('observer'),
					subtitle: this.displayTraitSlogan('observer'),
					colorOuter: '#28374A',
					colorInner: '#6489B9',
					badgeBgColorInactive: '#28374A',
					badgeBgColorActive: '#6489B9',
					iconText: 'obse'
				}
			]
		}
	},
	computed: {
		viewBox() {
			const viewBoxSize = this.size + this.viewBoxPadding * 2
			const origin = -this.viewBoxPadding

			return `${origin} ${origin} ${viewBoxSize} ${viewBoxSize}`
		},
		cx() {
			return this.size / 2
		},
		cy() {
			return this.size / 2
		},
		axisLabels() {
			const positions = [
				{
					id: 'facts',
					style: {
						left: '50%',
						top: '0',
						transform: 'translate(-50%, 0)'
					}
				},
				{
					id: 'change',
					x: this.cx + this.axisLabelRadius,
					y: this.cy + this.axisSideLabelYOffset,
					rotate: '90deg'
				},
				{
					id: 'people',
					style: {
						bottom: '18px',
						left: '50%',
						transform: 'translate(-50%, 0)'
					}
				},
				{
					id: 'routine',
					x: this.cx - this.axisLabelRadius,
					y: this.cy + this.axisSideLabelYOffset,
					rotate: '-90deg'
				}
			]

			return positions.map(label => ({
				id: label.id,
				style: label.style || this.axisLabelStyle(label),
				translationKey: `personality_results.axis_label.${label.id}`
			}))
		},
		applicantResultsByTrait() {
			return this.buildResultsMap(this.applicantResults || this.results)
		},
		jobOfferResultsByTrait() {
			return this.buildResultsMap(this.jobOfferResults || this.results)
		},
		activeResultsByTrait() {
			if (this.mode === 'job') {
				return this.jobOfferResultsByTrait
			}

			return this.applicantResultsByTrait
		},
		resultsByTrait() {
			const map = Object.create(null)

			for (const arc of this.baseArcs) {
				map[arc.name] = 0
			}

			for (const result of Object.entries(this.results)) {
				map[result[0]] = Number(result[1] ?? 0)
			}

			return map
		},
		arcs() {
			return this.baseArcs.map(arc => ({
				...arc,
				value: this.activeResultsByTrait[arc.name] ?? 0,
				applicantValue: this.applicantResultsByTrait[arc.name] ?? 0,
				jobOfferValue: this.jobOfferResultsByTrait[arc.name] ?? 0
			}))
		},
		arcCount() {
			return this.arcs.length
		},
		arcAngle() {
			return (Math.PI * 2) / this.arcCount
		},
		rotation() {
			return -Math.PI / this.arcs.length + this.arcAngle / 128
		},
		minInnerRadius() {
			return 0
		},
		maxInnerRadius() {
			return this.rOuter
		},
		deployProgress() {
			// Export capture renders a freshly mounted tree and grabs it one
			// tick later — the deploy entrance animation would still be at
			// ~0. Export captures always render the wheel fully deployed.
			if (this.isExportCapture) {
				return 1
			}
			return this.animated ? this.deployT : 1
		},
		isExportCapture() {
			return Boolean(this.personalityResultsExport?.exporting)
		},
		tooltipOpen() {
			return this.tooltipIndex !== null
		},
		tooltipArc() {
			return this.tooltipIndex !== null
				? this.arcs[this.tooltipIndex]
				: null
		}
	},
	methods: {
		axisCoordinateToPercent(value) {
			const viewBoxSize = this.size + this.viewBoxPadding * 2

			return ((value + this.viewBoxPadding) / viewBoxSize) * 100
		},
		axisLabelStyle(label) {
			const rotate = label.rotate ? ` rotate(${label.rotate})` : ''

			return {
				left: `${this.axisCoordinateToPercent(label.x)}%`,
				top: `${this.axisCoordinateToPercent(label.y)}%`,
				transform: `translate(-50%, -50%)${rotate}`
			}
		},
		buildResultsMap(resultsObject) {
			const map = Object.create(null)

			for (const arc of this.baseArcs) {
				map[arc.name] = 0
			}

			if (!resultsObject) {
				return map
			}

			for (const [trait, value] of Object.entries(resultsObject)) {
				map[trait] = Number(value ?? 0)
			}

			return map
		},
		modeIsComparison() {
			return this.mode === 'comparison'
		},
		startAngle(index) {
			return index * this.arcAngle + this.rotation
		},
		startAngleHalf(index, which) {
			// which: 'applicant' | 'job'
			const start = this.startAngle(index)
			const end = this.endAngle(index)

			const mid = (start + end) / 2

			if (which === 'job') {
				return start
			}

			return mid
		},
		endAngle(index) {
			return (index + 1) * this.arcAngle + this.rotation
		},
		endAngleHalf(index, which) {
			const start = this.startAngle(index)
			const end = this.endAngle(index)

			const mid = (start + end) / 2

			if (which === 'job') {
				return mid
			}

			return end
		},
		clamp(value, min, max) {
			return Math.max(min, Math.min(max, value))
		},
		valuePercent(index) {
			const value = this.arcs[index].value ?? 0
			return this.clamp(value, 0, 100)
		},
		valuePercentFor(index, which) {
			const arc = this.arcs[index]

			const raw =
				which === 'applicant'
					? arc.applicantValue
					: which === 'job'
					? arc.jobOfferValue
					: arc.value

			return this.clamp(raw ?? 0, 0, 100)
		},
		separatorPath(index) {
			const start = this.startAngleAnim(index)
			const end = this.endAngleAnim(index)
			const mid = (start + end) / 2

			const angle = mid - Math.PI / 2

			const r0 = this.innerInnerRadiusAnim()
			const r1 = this.outerRadiusAnim()

			const x0 = Math.cos(angle) * r0
			const y0 = Math.sin(angle) * r0
			const x1 = Math.cos(angle) * r1
			const y1 = Math.sin(angle) * r1

			return `M ${x0} ${y0} L ${x1} ${y1}`
		},
		innerOuterRadius(index) {
			const percent = this.valuePercent(index)
			const ratio = percent / 100

			const easedRatio = Math.pow(ratio, 0.3) // Make lower values 'pop' more

			return (
				this.minInnerRadius +
				easedRatio * (this.maxInnerRadius - this.minInnerRadius)
			)
		},
		innerOuterRadiusFor(index, which) {
			const percent = this.valuePercentFor(index, which)
			const ratio = percent / 100
			const easedRatio = Math.pow(ratio, 0.3)

			return (
				this.minInnerRadius +
				easedRatio * (this.maxInnerRadius - this.minInnerRadius)
			)
		},
		outerPath(index) {
			const diameter = this.arcGen({
				innerRadius: this.outerInnerRadiusAnim(),
				outerRadius:
					this.outerRadiusHoverAnim(index) *
					this.hoverScaleFor(index),
				startAngle: this.startAngleAnim(index),
				endAngle: this.endAngleAnim(index)
			})

			return diameter || ''
		},
		innerPath(index) {
			const outerRadius = this.innerOuterRadiusAnim(index)

			if (outerRadius <= 0) {
				return ''
			}

			const diameter = this.arcGen({
				innerRadius: this.innerInnerRadiusAnim(),
				outerRadius,
				startAngle: this.startAngleAnim(index),
				endAngle: this.endAngleAnim(index)
			})

			return diameter || ''
		},
		innerPathHalf(index, which) {
			const outerRadius =
				this.innerOuterRadiusFor(index, which) * this.deployProgress

			if (outerRadius <= 0) {
				return ''
			}

			const diameter = this.arcGen({
				innerRadius: this.innerInnerRadiusAnim(),
				outerRadius,
				startAngle: this.interpolate(
					this.collapsedAngle(),
					this.startAngleHalf(index, which),
					this.deployProgress
				),
				endAngle: this.interpolate(
					this.collapsedAngle(),
					this.endAngleHalf(index, which),
					this.deployProgress
				)
			})

			return diameter || ''
		},
		iconPos(index) {
			const mid =
				this.startAngleAnim(index) +
				(this.arcAngle * this.deployProgress) / 2

			const angle = mid - Math.PI / 2

			const totalBadgeRadius =
				this.badgeRadius + this.badgeStrokeWidth / 2

			const scaledOuter = this.rOuter * this.hoverScaleFor(index)

			const finalRadius =
				scaledOuter + this.badgeRadius - totalBadgeRadius

			const radius = finalRadius * this.deployProgress

			return {
				x: Math.cos(angle) * radius,
				y: Math.sin(angle) * radius
			}
		},
		getIconComponent(name) {
			return getTraitIcon(name)
		},
		getBadgeBgColor(arc) {
			return arc.value > 0
				? arc.badgeBgColorActive
				: arc.badgeBgColorInactive
		},
		getBadgeStrokeColor(arc) {
			return arc.value > 0
				? this.badgeStrokeColorActive
				: this.badgeStrokeColorInactive
		},
		collapsedAngle() {
			return -Math.PI / 2
		},
		interpolate(a, b, t) {
			return a + (b - a) * t
		},
		startAngleAnim(index) {
			return this.interpolate(
				this.collapsedAngle(),
				this.startAngle(index),
				this.deployProgress
			)
		},
		endAngleAnim(index) {
			return this.interpolate(
				this.collapsedAngle(),
				this.endAngle(index),
				this.deployProgress
			)
		},
		outerRadiusAnim() {
			return this.rOuter * this.deployProgress
		},
		outerInnerRadiusAnim() {
			return this.rOuterInner * this.deployProgress
		},
		innerInnerRadiusAnim() {
			return this.rInnerInner * this.deployProgress
		},
		innerOuterRadiusAnim(index) {
			return this.innerOuterRadius(index) * this.deployProgress
		},
		isHovered(index) {
			return this.hoverIndex === index
		},
		outerRadiusHoverAnim(index) {
			const base = this.outerRadiusAnim()

			if (!this.isHovered(index)) {
				return base
			}

			return base * this.hoverScale
		},
		hoverScaleFor(index) {
			return 1 + (this.hoverScale - 1) * this.hoverProgress(index)
		},
		hoverProgress(index) {
			return this.hoverProgressByIndex[index] ?? 0
		},
		easeOutCubic(t) {
			return 1 - Math.pow(1 - t, 3)
		},
		tweenHover(index, to) {
			const from = this.hoverProgress(index)

			const prev = this.hoverRAFByIndex[index]

			if (prev) {
				cancelAnimationFrame(prev)
			}

			const start = performance.now()
			const duration = this.hoverMs

			const tick = now => {
				const raw = (now - start) / duration
				const clamped = Math.max(0, Math.min(1, raw))
				const eased = this.easeOutCubic(clamped)

				this.$set
					? this.$set(
							this.hoverProgressByIndex,
							index,
							this.interpolate(from, to, eased)
					  )
					: (this.hoverProgressByIndex[index] = this.interpolate(
							from,
							to,
							eased
					  ))

				if (clamped < 1) {
					this.hoverRAFByIndex[index] = requestAnimationFrame(tick)
				} else {
					this.hoverRAFByIndex[index] = null
				}
			}

			this.hoverRAFByIndex[index] = requestAnimationFrame(tick)
		},
		onArcEnter(index) {
			for (let i = 0; i < this.arcCount; i++) {
				if (i !== index) {
					this.tweenHover(i, 0)
				}
			}

			this.tweenHover(index, 1)

			const badge = this.getBadgeElement(index)

			if (!badge) {
				return
			}

			this.tooltipRef = this.virtualPointFromBadge(badge)

			this.tooltipIndex = index
		},
		onArcLeave(index) {
			this.tweenHover(index, 0)

			if (this.tooltipIndex === index) {
				this.tooltipIndex = null
				this.tooltipRef = null
			}
		},
		getBadgeElement(index) {
			const ref = this.$refs[`badge-${index}`]

			return Array.isArray(ref) ? ref[0] : ref
		},
		virtualPointFromBadge(badge) {
			return {
				getBoundingClientRect: () => {
					const r = badge.getBoundingClientRect()
					const x = r.left + r.width / 2
					const y = r.top + r.height / 2

					// 0x0 rect at the badge center
					return {
						x,
						y,
						width: 0,
						height: 0,
						top: y,
						left: x,
						right: x,
						bottom: y
					}
				},
				contextElement: badge
			}
		},
		displayTraitName(trait) {
			if (!trait) {
				return ''
			}

			return TRAIT_LABELS[trait]
		},
		displayTraitSlogan(trait) {
			if (!trait) {
				return ''
			}

			const slogan = this.$t(`personality_results.trait.${trait}.slogan`)

			const [firstSentence] = slogan.match(/[^.!?]+[.!?]?/u) || []

			return firstSentence ? firstSentence.trim() : slogan.trim()
		}
	},
	mounted() {
		if (!this.animated || this.isExportCapture) {
			this.deployT = 1

			return
		}

		const prefersReduced =
			typeof window !== 'undefined' &&
			window.matchMedia &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches

		if (prefersReduced) {
			this.deployT = 1

			return
		}

		this.deployT = 0

		const start = performance.now()
		const duration = this.deployMs

		const easeOutCubic = t => 1 - Math.pow(1 - t, 3)

		const tick = now => {
			const raw = (now - start) / duration
			const clamped = Math.max(0, Math.min(1, raw))
			this.deployT = easeOutCubic(clamped)

			if (clamped < 1) {
				requestAnimationFrame(tick)
			}
		}

		requestAnimationFrame(tick)
	}
}
</script>
